import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Link, matchPath, Navigate, useLocation, useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { NAV, NavItem } from "./nav";
import logo from "assets/logo/logo_white.png";
import { ReactComponent as ArrowRight } from "assets/console/arrow_right_white.svg";
import { useConsoleAuthStore } from "store/useConsoleAuthStore";
import { SUPER_ADMIN_IDS } from "../SuperAdminGuard";

export type SideBarRef = {
    isReady: () => boolean;
};

const SideBar = forwardRef<SideBarRef>((_, ref) => {
    const location = useLocation();
    // console.log(location.pathname);
    const navigate = useNavigate();
    const [activeOneDepth, setActiveOneDepth] = useState<string | null>(null);
    const [activeTwoDepth, setActiveTwoDepth] = useState<NavItem[] | null>(null);
    const [ready, setReady] = useState(false);
    // console.log(activeOneDepth);

    // 새로고침 대응
    useEffect(() => {
        const pathArray = location.pathname.split("/").filter(Boolean);
        const oneDepth = "/" + pathArray[0];
        if (oneDepth) setActiveOneDepth(oneDepth);
    }, [location.pathname]);

    useEffect(() => {
        if (activeOneDepth) {
            const menu = NAV?.find(menu => menu.path === activeOneDepth)?.sub_menu;
            if (menu) setActiveTwoDepth(menu);
        } else {
            setActiveTwoDepth(null);
        }
        setReady(true);
    }, [activeOneDepth]);

    // drawer 열기
    // 대메뉴가 바뀔때마다 실행, 활성화된 대메뉴에 해당하는 서브 메뉴 가져오기
    const toggleTwoDepth = (toggleMenu: string | null) => {
        if (toggleMenu) {
            const menu = NAV?.find(menu => menu.path === toggleMenu)?.sub_menu;

            if (menu) {
                setActiveTwoDepth(prev => (prev === menu ? null : menu));

                // 메뉴 클릭시 첫번째 서브 메뉴로 이동
                const firstMenu = menu[0];
                let path = "";
                if (firstMenu.sub_menu) {
                    path = firstMenu.sub_menu[0].path[0].toString();
                }

                // return <Navigate to={path} replace />;
                navigate(path);
            }
        } else {
            setActiveTwoDepth(null);
        }
    };

    useImperativeHandle(ref, () => ({
        isReady: () => ready,
    }));

    const handleLogout = () => {
        useConsoleAuthStore.getState().clearAuth();
    };

    const a_id = useConsoleAuthStore(state => state.a_id);
    const isSuperAdmin = !!a_id && SUPER_ADMIN_IDS.includes(a_id);
    const isAdminMenuActive = location.pathname.startsWith("/admin");

    return (
        <div className="flex min-h-[100dvh] max-h-[100dvh] overflow-hidden">
            <div className="bg-black px-[8px] overflow-auto flex flex-col justify-between gap-[40px]">
                <div>
                    <h2 className="mb-[16px]">
                        <Link to="/" className="flex justify-center py-[13px]">
                            <img src={logo} alt="일신그룹" />
                            <span className="indent-[-9999em] h-0 absolute">일신그룹</span>
                        </Link>
                    </h2>
                    <nav>
                        <ul className="flex flex-col gap-y-[24px]">
                            {NAV.map(item => {
                                const isActive = activeOneDepth === item.path;
                                const activeStyle = twMerge(
                                    `relative w-[64px] h-[64px] text-white font-bold text-[14px] rounded-[12px] flex items-center justify-center`,
                                    isActive && "bg-[linear-gradient(137.22deg,#FE9257_50.75%,#FE8441_50.76%)]",
                                );
                                return (
                                    <li
                                        key={item.menu}
                                        className={activeStyle}
                                        onClick={() => {
                                            setActiveOneDepth(item.path.toString());
                                        }}
                                    >
                                        {item.sub_menu ? (
                                            <div
                                                className="flex-1 h-full flex flex-col gap-y-[4px] justify-center items-center cursor-pointer"
                                                onClick={() => toggleTwoDepth(item.path.toString())}
                                            >
                                                {item.icon}
                                                <span>{item.menu}</span>
                                            </div>
                                        ) : (
                                            <div
                                                onClick={() => toggleTwoDepth(null)}
                                                className="flex-1 h-full flex justify-center items-center"
                                            >
                                                <Link
                                                    to={item.path.toString()}
                                                    className="flex-1 h-full flex flex-col gap-y-[4px] justify-center items-center"
                                                >
                                                    {item.icon}
                                                    <span>{item.menu}</span>
                                                </Link>
                                            </div>
                                        )}
                                        {!isActive && <span className="absolute bg-black/40 inset-0 pointer-events-none"></span>}
                                    </li>
                                );
                            })}
                        </ul>
                    </nav>
                </div>

                <div className="mt-auto mb-[40px] flex flex-col gap-[12px]">
                    {isSuperAdmin && (
                        <button
                            type="button"
                            className={twMerge(
                                "text-[#F3F3F8] text-[14px] w-full underline",
                                isAdminMenuActive && "font-bold text-white",
                            )}
                            onClick={() => {
                                setActiveOneDepth(null);
                                setActiveTwoDepth(null);
                                navigate("/admin/account");
                            }}
                        >
                            관리자
                        </button>
                    )}
                    <button className="text-[#F3F3F8] text-[14px] w-full underline" onClick={handleLogout}>
                        로그아웃
                    </button>
                </div>
            </div>
            {activeTwoDepth && (
                <div className="flex flex-col gap-[10px] bg-[#1B1A1F] p-4 shrink-0 min-w-[194px] overflow-auto">
                    {activeTwoDepth?.map((item, index) => {
                        const path = item.path;
                        const isActive = Array.isArray(path) && path.some(pattern => matchPath(pattern, location.pathname));

                        return (
                            <details key={index} className="[&:open>summary]:bg-white/10" open={isActive}>
                                <summary
                                    className="py-[21px] rounded-[8px] flex items-center text-white text-[18px] font-[600] px-[12px] gap-[12px] cursor-pointer"
                                    onClick={() =>
                                        !item.sub_menu &&
                                        (location.pathname === item.path.toString() ? navigate(0) : navigate(item.path.toString()))
                                    }
                                >
                                    {item.icon}
                                    <span>{item.menu}</span>
                                    <ArrowRight />
                                </summary>
                                {item.sub_menu && (
                                    <div className="flex flex-col my-[10px] pl-[12px]">
                                        {item.sub_menu.map(i => {
                                            const path = Array.isArray(i.path) ? i.path[0] : i.path;
                                            const pathArray = Array.isArray(i.path) ? i.path : [i.path];
                                            const isActive = pathArray.some(pattern => matchPath(pattern, location.pathname));
                                            return (
                                                <Link
                                                    to={path}
                                                    key={i.menu}
                                                    // reloadDocument={location.pathname === path}
                                                    state={{ reset: location.pathname === path }}
                                                    className={`text-white/80 font-[500] py-[10px] ${isActive && "underline"}`}
                                                >
                                                    {i.menu}
                                                </Link>
                                            );
                                        })}
                                    </div>
                                )}
                            </details>
                        );
                    })}
                </div>
            )}
        </div>
    );
});

export default SideBar;
